import { useEffect, useState, ChangeEvent } from "react";
import { Alert, Input, Table } from "reactstrap";
import useToggle from "../../../../hook/useToggle";
import { CancelIcon, CheckMarkIcon, EditIcon } from "../../../common/icons";
import { IAddressUsage } from "../../../../types/contact";
import AddressUpdate from "./address-update";
import { useLazyQuery, useMutation } from "@apollo/client";
import SpinnerOverlay from "../../../common/spinner/spinner";
import { CheckGroup } from "../../../../hook/useCheckGroup";
import { notifySuccess } from "../../../../services/notification";
import { GET_POSITION_ADDRESSES, SET_POSITION_MAILING_ADDRESS, SET_POSITION_PRIMARY_ADDRESS, UNSET_POSITION_MAILING_ADDRESS, UNSET_POSITION_PRIMARY_ADDRESS } from "../../../../graphql/contact-gql";
import Info from "../../../common/info";
import { IPosition } from "../../../../types/position";
import ImgButton from "../../../common/img-button/img-button";
import useAuth from "../../../../hook/useAuth";
import { GET_ORG_BY_POSITION } from "../../../../graphql/position-gql";
import { IOrg, IOrgResponse } from "../../../../types/org";
import { GET_ORG_DETAILS } from "../../../../graphql/org-gql";
import { CategoryType } from "../../../../types/constants";
import { useFrench } from "../../../french/french-context";
import { NamedEntity } from "../../../../types/common";

type PositionAddressProps = {
    position: IPosition;
    checkGroup: CheckGroup<number>;
};

const PositionAddress = (props: PositionAddressProps ) => {
    const {position, checkGroup} = props
    const {isOpen: editAddressOpen, toggle: toggleEditAddress} = useToggle()
    const { isFrenchTranslator } = useAuth()
    const [frenchState] = useFrench()
    const [positionAddress, setPositionAddress] = useState<IAddressUsage>()

    const [getPositionAddresses, {loading, data}] = useLazyQuery(GET_POSITION_ADDRESSES, {
        onCompleted: (data) => {
            if (data?.position?.addressUsages) {
                checkGroup.init(data.position.addressUsages.filter((e: IAddressUsage) => !e.orgAddress).map((e: IAddressUsage) => e.id))
            }
        }
    })

    const [getOrgByPosition, { loading: positionLoading }] = useLazyQuery<{ orgByPosition: NamedEntity }>(GET_ORG_BY_POSITION);
    const [getOrgDetail, { loading: loadingOrg }] = useLazyQuery<IOrgResponse>(GET_ORG_DETAILS);

    const [setPrimaryAddress, {loading: loadingPrimary}] = useMutation(SET_POSITION_PRIMARY_ADDRESS)
    const [unsetPrimaryAddress, {loading: loadingNotPrimary}] = useMutation(UNSET_POSITION_PRIMARY_ADDRESS)
    const [setMailingAddress, {loading: loadingMailing}] = useMutation(SET_POSITION_MAILING_ADDRESS)
    const [unsetMailingAddress, {loading: loadingNotMailing}] = useMutation(UNSET_POSITION_MAILING_ADDRESS)
    const [organization, setOrganization] = useState<IOrg>();

    // track if the on-site and mailing address checkboxes get checked by the user, not if they are already checked when loaded
    const [hasSetOnSiteAddress, setOnSiteAddress] = useState<Boolean>()
    const [hasSetAsMailingAddress, setAsMailingAddress] = useState<Boolean>()
    const [cloned,setCloned] = useState<boolean>(false);

    useEffect(() => {
        setCloned(!!position?.shareLinkId)
        if (position) {
            getPositionAddresses({variables: {positionId: position.positionId}})

            getOrgByPosition({variables: {positionId: position.positionId}}).then(response => {
                if(response.data){
                    getOrgDetail({
                        variables: {
                          id: response.data.orgByPosition.id
                        }
                      }).then(response => setOrganization(response.data?.org));
                }
             }
            )
        }
    }, [position]);

    const editAddress = (address: IAddressUsage) => {
        setPositionAddress(address)
        toggleEditAddress()
    }

    const updatePrimary = (event: ChangeEvent<HTMLInputElement>, address: IAddressUsage) => {
        if (event.target.checked) {
            setPrimaryAddress({
                variables: {
                    positionId: address.businessEntityId,
                    addressUsageId: address.id
                },
                refetchQueries: [{
                    query: GET_POSITION_ADDRESSES,
                    variables: { positionId: address.businessEntityId }
                }],
                awaitRefetchQueries: true,
                onCompleted: (data) => data && notifySuccess()
            }).then(() => {
                setOnSiteAddress(true);
            })
        } else {
            unsetPrimaryAddress({
                variables: {
                    positionId: address.businessEntityId,
                    addressUsageId: address.id
                },
                refetchQueries: [{
                    query: GET_POSITION_ADDRESSES,
                    variables: { positionId: address.businessEntityId }
                }],
                awaitRefetchQueries: true,
                onCompleted: (data) => data && notifySuccess()
            }).then(() => {
                setOnSiteAddress(false);
            })
        }
    }

    const updateMailing = (event: ChangeEvent<HTMLInputElement>, address: IAddressUsage) => {
        if (event.target.checked) {
            setMailingAddress({
                variables: {
                    positionId: address.businessEntityId,
                    addressUsageId: address.id
                },
                refetchQueries: [{
                    query: GET_POSITION_ADDRESSES,
                    variables: { positionId: address.businessEntityId }
                }],
                awaitRefetchQueries: true,
                onCompleted: (data) => data && notifySuccess()
            }).then(() => {
                setAsMailingAddress(true);
            });
        } else {
            unsetMailingAddress({
                variables: {
                    positionId: address.businessEntityId,
                    addressUsageId: address.id
                },
                refetchQueries: [{
                    query: GET_POSITION_ADDRESSES,
                    variables: { positionId: address.businessEntityId }
                }],
                awaitRefetchQueries: true,
                onCompleted: (data) => data && notifySuccess()
            }).then(() => {
                setAsMailingAddress(false);
            });
        }
    }

    const visibleColumnsCount = () : number => {
        // french translator won't see the select or edit columns
        return isFrenchTranslator ? 6 : 8;
    }

    return (
        <div>
            {(loading || loadingPrimary || loadingMailing || loadingNotPrimary || loadingNotMailing || positionLoading || loadingOrg) && <SpinnerOverlay />}

            {data?.position?.addressUsages && (<>
                <Info style={{marginTop: '0'}}>
                    Only on-site and mailing addresses will be shown on INFO-GO.<br/>
                    If on-site or mailing are unchecked, they will be defaulted to the relevant organization address.
                </Info>

                <Table striped id="position_addresses" className="fixed">
                    <thead>
                        <tr>
                            {!isFrenchTranslator && <th className="icon-column">
                                <span className="sr-only">select address</span>
                                {data?.position?.addressUsages?.length > 0 && !checkGroup.isEmpty() &&
                                <>
                                    <span id="posAdrSelectAllLbl" className="sr-only">Select all</span>
                                    <Input
                                        type="checkbox"
                                        title="Select all"
                                        aria-labelledby="posAdrSelectAllLbl"
                                        checked={checkGroup.isAllChecked()}
                                        onChange={checkGroup.onCheckAll}
                                        disabled={cloned}
                                    />
                                </>
                                }
                            </th>
                            }
                            <th>Address</th>
                            <th>City</th>
                            <th className="column-7rem">Postal Code</th>
                            <th className="column-7rem">Org Address</th>
                            <th className="primary-mailing-column">On-Site</th>
                            <th className="primary-mailing-column">Mailing</th>
                            { (!isFrenchTranslator  || frenchState.isFrenchChecked) && <th className="edit-column">Edit</th>}
                        </tr>
                    </thead>
                    <tbody>
                    {
                        data?.position?.addressUsages?.length === 0
                        ?
                        <tr><td colSpan={visibleColumnsCount()} style={{textAlign: 'center'}}>No {position.isRole ? 'role' : 'position'} addresses found.</td></tr>
                        :
                        data?.position?.addressUsages?.map(
                            (address: IAddressUsage, index: number) => (
                                <tr key={address.id}>
                                   {!isFrenchTranslator && <td>
                                        {!address.orgAddress  &&
                                        <Input type="checkbox"
                                            aria-label={'Select ' + address.addressTxt}
                                            checked={checkGroup.isChecked(address.id)}
                                            onChange={event=>checkGroup.onCheck(event, address.id)}
                                            disabled={cloned}
                                        />
                                        }
                                    </td>
                                    }
                                    <td>{address.addressTxt}</td>
                                    <td>{address.city}</td>
                                    <td>{address.postalCode}</td>
                                    <td>{address.orgAddress && <CheckMarkIcon title="Org address"></CheckMarkIcon>}</td>
                                    {
                                        !isFrenchTranslator &&  <td>
                                            {
                                              organization?.details?.category.type == CategoryType.ORGANIZATION &&<>
                                              {
                                                address.orgAddress
                                                    ? address.primary && <CheckMarkIcon title="On-site address"></CheckMarkIcon>
                                                    : <Input type="checkbox"
                                                        aria-label="Set on-site address"
                                                        checked={address.primary}
                                                        onChange={(event) => updatePrimary(event, address)}
                                                        disabled={cloned}/>
                                            }
                                            </>
                                           }
                                           {
                                              organization?.details?.category.type != CategoryType.ORGANIZATION &&<>
                                              {
                                                 address.primary? <CheckMarkIcon title="On-site address"></CheckMarkIcon>
                                                    : <Input type="checkbox"
                                                        aria-label="Set on-site address"
                                                        checked={address.primary}
                                                        onChange={(event) => updatePrimary(event, address)}
                                                        disabled={cloned}/>
                                            }
                                            </>
                                           }
                                           <span className="sr-only" aria-live="assertive">{
                                            (organization?.details?.category.type == CategoryType.ORGANIZATION && address.orgAddress && address.primary && hasSetOnSiteAddress)
                                            ||
                                            (organization?.details?.category.type != CategoryType.ORGANIZATION && address.primary  && hasSetOnSiteAddress )
                                            ? "checked" : "" }
                                           </span>
                                        </td>
                                    }{
                                        isFrenchTranslator && <td>
                                           {
                                              organization?.details?.category.type == CategoryType.ORGANIZATION &&<>
                                            {
                                                address.orgAddress
                                                    ? address.primary && <CheckMarkIcon title="On-site address"></CheckMarkIcon>
                                                    : <CancelIcon></CancelIcon>
                                            }
                                            </>
                                           }
                                           {
                                              organization?.details?.category.type != CategoryType.ORGANIZATION &&<>
                                            {
                                                 address.primary?  <CheckMarkIcon title="Primary"></CheckMarkIcon>
                                                    : <CancelIcon></CancelIcon>
                                            }
                                            </>
                                           }
                                        </td>
                                    }
                                    {
                                        !isFrenchTranslator && <td>
                                            {
                                              organization?.details?.category.type == CategoryType.ORGANIZATION &&<>
                                            {
                                                address.orgAddress
                                                    ? address.mailing && <CheckMarkIcon title="Mailing address"></CheckMarkIcon>
                                                    : <Input type="checkbox"
                                                        aria-label="Set mailing address"
                                                        checked={address.mailing}
                                                        onChange={(event) => updateMailing(event, address)}
                                                        disabled={cloned}/>
                                            }
                                            </>
                                           }
                                           {
                                              organization?.details?.category.type != CategoryType.ORGANIZATION &&<>
                                            {

                                                     address.mailing?  <CheckMarkIcon title="Mailing address"></CheckMarkIcon>
                                                    : <Input type="checkbox"
                                                        aria-label="Set mailing address"
                                                        checked={address.mailing}
                                                        onChange={(event) => updateMailing(event, address)}
                                                        disabled={cloned}/>
                                            }
                                            </>
                                           }
                                           <span className="sr-only" aria-live="assertive">{
                                            (organization?.details?.category.type == CategoryType.ORGANIZATION && address.orgAddress && address.mailing && hasSetAsMailingAddress)
                                            ||
                                            (organization?.details?.category.type != CategoryType.ORGANIZATION && address.mailing  && hasSetAsMailingAddress )
                                            ? "checked" : "" }
                                           </span>
                                        </td>
                                    }
                                    {
                                        isFrenchTranslator && <td>
                                         {
                                              organization?.details?.category.type == CategoryType.ORGANIZATION &&<>
                                            {
                                                address.orgAddress
                                                    ? address.mailing && <CheckMarkIcon title="Mailing address"></CheckMarkIcon> : <CancelIcon></CancelIcon>
                                            }
                                            </>
                                        }
                                        {
                                              organization?.details?.category.type != CategoryType.ORGANIZATION &&<>
                                            {
                                                 address.mailing? <CheckMarkIcon title="Mailing address"></CheckMarkIcon> : <CancelIcon></CancelIcon>
                                            }
                                            </>
                                        }
                                        </td>
                                    }
                                    { (!isFrenchTranslator || frenchState.isFrenchChecked) && <td>
                                        { !address.orgAddress &&
                                            <ImgButton className="float-end" aria-label="Edit address" title="Edit address" onClick={()=>editAddress(address)} disabled={cloned}>
                                                <EditIcon className="edit-pencil"/>
                                            </ImgButton>
                                        }
                                    </td>}
                                </tr>
                            )
                        )}
                    </tbody>
                </Table>
            </>
            )}

            <AddressUpdate addressUsage={positionAddress} isRole={position?.isRole} isOpen={editAddressOpen} toggle={toggleEditAddress}/>
        </div>
    )
}

export default PositionAddress;
